/**
 * Google Tag Manager dataLayer utility.
 * Provides type-safe functions to push events and user identity to GTM.
 * Stores user context and auto-appends to every push.
 *
 * Internally uses camelCase; maps to GA4-standard snake_case at push time.
 */

import type {
  GtmEcommerceData,
  GtmEcommerceItem,
  GtmUserProperties,
} from '../interfaces/gtm.interface'
import { logger } from './logger.util'

let storedUserId: string | undefined
let storedOrgId: string | undefined

const getDataLayer = (): Record<string, unknown>[] => {
  const win: Window & { dataLayer?: Record<string, unknown>[] } = window
  win.dataLayer = win.dataLayer ?? []
  return win.dataLayer
}

const buildUserContext = (): Record<string, string | undefined> => ({
  user_id: storedUserId,
  org_id: storedOrgId,
})

export const pushToDataLayer = (eventName: string, data: Record<string, unknown> = {}): void => {
  const payload = { event: eventName, ...buildUserContext(), ...data }
  getDataLayer().push(payload)
  logger.debug('[GTM] Event pushed', eventName, payload)
}

export const setGtmUser = (properties: GtmUserProperties): void => {
  if (!properties.userId) return

  if (storedUserId === properties.userId && storedOrgId === properties.orgId) return

  storedUserId = properties.userId
  storedOrgId = properties.orgId
  pushToDataLayer('user_properties_set', {
    org_slug: properties.orgSlug,
  })
}

export const clearGtmUser = (): void => {
  storedUserId = undefined
  storedOrgId = undefined
}

export const pushGtmSignUp = (method: string): void => {
  pushToDataLayer('sign_up', { method })
}

export const pushGtmLogin = (method: string): void => {
  pushToDataLayer('login', { method })
}

const mapEcommerceItem = (item: GtmEcommerceItem): Record<string, unknown> => ({
  item_id: item.itemId,
  item_name: item.itemName,
  item_category: item.itemCategory,
  price: item.price,
  quantity: item.quantity,
})

export const pushGtmPurchase = (ecommerce: GtmEcommerceData): void => {
  pushToDataLayer('purchase', {
    ecommerce: {
      transaction_id: ecommerce.transactionId,
      value: ecommerce.value,
      currency: ecommerce.currency,
      items: ecommerce.itemList.map(mapEcommerceItem),
    },
  })
}

export const pushGtmTutorialComplete = (): void => {
  pushToDataLayer('tutorial_complete')
}
